import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { mcsApi } from '../../../api/mcs/mcsApi';
import { IOrderLocationGroup } from '../../../api/mcs/interfaces';
import { notifyError } from '../../../api/axios';
import { RootState } from '../../store';
import { t } from 'i18next';
import { IOrderReviewRow } from '../../../api/mcs/interfaces';

interface IOrderAndReviewModalState {
    reviewOrders: IOrderReviewRow[];
    loading: boolean;
    error: string | null;
}

const initialState: IOrderAndReviewModalState = {
    reviewOrders: [],
    loading: false,
    error: null,
};

const combineDateAndTime = (dateString: string, timeString: string): string => {
    const datePart = dateString.split('T')[0];
    return `${datePart}T${timeString}`;
};

const mapToOrderReviewRows = (
    orderGroups: IOrderLocationGroup[],
    existingOrderIds?: number[],
): IOrderReviewRow[] => {
    const existingOrderIdSet = new Set(existingOrderIds || []);

    const flattenedRows = orderGroups.flatMap(group => {
        const isNewlyAdded =
            existingOrderIds && existingOrderIds.length > 0
                ? !existingOrderIdSet.has(group.orderDetails.orderId)
                : false;

        return group.orderDetails.locations.map(location => ({
            orderId: group.orderDetails.orderId,
            location: {
                city: location.city,
                state: location.state,
            },
            locationType: location.locationType,
            orderLocationId: location.orderLocationId,
            customers: [group.orderDetails.customerName],
            commodities: location.commodities ?? [],
            dateTime: combineDateAndTime(
                group.orderDetails.deliveryStartDate,
                group.orderDetails.loadTime,
            ),
            shipmentOrdinal: location.shipmentOrdinal,
            ordinal: location.ordinal,
            isNewlyAdded,
        }));
    });

    const rowsWithOrdinals = flattenedRows.map((row, index) => ({
        ...row,
        ordinal: row.shipmentOrdinal ?? row.ordinal ?? index,
    }));

    const mergedRowsMap = new Map<number, IOrderReviewRow>();
    const unmatchedRows: IOrderReviewRow[] = [];

    rowsWithOrdinals.forEach(row => {
        if (row.shipmentOrdinal == null) {
            unmatchedRows.push(row);
            return;
        }

        if (row.isNewlyAdded) {
            unmatchedRows.push(row);
            return;
        }

        const existingRow = mergedRowsMap.get(row.shipmentOrdinal);
        if (existingRow) {
            mergedRowsMap.set(row.shipmentOrdinal, {
                ordinal: existingRow.ordinal,
                orderId: [
                    ...normalizeToArray(existingRow.orderId),
                    ...normalizeToArray(row.orderId),
                ],
                location: existingRow.location,
                locationType: existingRow.locationType,
                orderLocationId: [
                    ...normalizeToArray(existingRow.orderLocationId),
                    ...normalizeToArray(row.orderLocationId),
                ],
                customers: [
                    ...normalizeToArray(existingRow.customers),
                    ...normalizeToArray(row.customers),
                ],
                commodities: [
                    ...normalizeToArray(existingRow.commodities),
                    ...normalizeToArray(row.commodities),
                ],
                dateTime: existingRow.dateTime,
                shipmentOrdinal: row.shipmentOrdinal,
            });
        } else {
            mergedRowsMap.set(row.shipmentOrdinal, row);
        }
    });

    return [...Array.from(mergedRowsMap.values()), ...unmatchedRows].sort(
        (a, b) => a.ordinal - b.ordinal,
    );
};

export const getReviewOrders = createAsyncThunk(
    'mcs/reviewOrders/getReviewOrders',
    async (
        payload: { orderIds: number[]; existingOrderIds?: number[] },
        { rejectWithValue },
    ) => {
        try {
            const response = await mcsApi.reviewOrders.getOrdersToReview(
                payload.orderIds,
            );

            return mapToOrderReviewRows(
                response.orderLocationGroups,
                payload.existingOrderIds,
            );
        } catch (error: unknown) {
            notifyError(t('failedToFetchOrders'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('failedToFetchOrders');
            return rejectWithValue(message);
        }
    },
);
export const normalizeToArray = <T>(value: T | T[]): T[] => {
    return Array.isArray(value) ? (value as T[]) : [value as T];
};

const reviewOrdersSlice = createSlice({
    name: 'mcsReviewOrders',
    initialState,
    reducers: {
        clearError: state => {
            state.error = null;
        },
        mergeLocationRows: (
            state,
            action: PayloadAction<{
                primaryRowIndex: number;
                comparisonRowIndex: number;
            }>,
        ) => {
            const { primaryRowIndex, comparisonRowIndex } = action.payload;
            const primaryRow = state.reviewOrders[primaryRowIndex];
            const comparisonRow = state.reviewOrders[comparisonRowIndex];

            if (!primaryRow || !comparisonRow) return;

            const mergedRow: IOrderReviewRow = {
                ordinal: Math.min(primaryRow.ordinal, comparisonRow.ordinal),
                orderId: [
                    ...normalizeToArray(primaryRow.orderId),
                    ...normalizeToArray(comparisonRow.orderId),
                ],
                location: primaryRow.location,
                locationType: primaryRow.locationType,
                orderLocationId: [
                    ...normalizeToArray(primaryRow.orderLocationId),
                    ...normalizeToArray(comparisonRow.orderLocationId),
                ],
                customers: [
                    ...normalizeToArray(primaryRow.customers),
                    ...normalizeToArray(comparisonRow.customers),
                ],
                commodities: [
                    ...normalizeToArray(primaryRow.commodities),
                    ...normalizeToArray(comparisonRow.commodities),
                ],
                dateTime: primaryRow.dateTime,
                shipmentOrdinal:
                    primaryRow.shipmentOrdinal ?? comparisonRow.shipmentOrdinal,
                isNewlyAdded: false,
            };

            const insertionIndex = Math.min(
                primaryRowIndex,
                comparisonRowIndex,
            );
            const indicesToDelete = [primaryRowIndex, comparisonRowIndex].sort(
                (deletionIndexA, deletionIndexB) =>
                    deletionIndexB - deletionIndexA,
            );

            indicesToDelete.forEach(deletionIndex => {
                state.reviewOrders.splice(deletionIndex, 1);
            });

            state.reviewOrders.splice(insertionIndex, 0, mergedRow);
        },
    },
    extraReducers: builder => {
        builder.addCase(getReviewOrders.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getReviewOrders.fulfilled, (state, action) => {
            state.loading = false;
            state.reviewOrders = action.payload;
        });
        builder.addCase(getReviewOrders.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    },
});

export const { clearError, mergeLocationRows } = reviewOrdersSlice.actions;

export const selectReviewOrders = (state: RootState): IOrderReviewRow[] =>
    state.mcsReviewOrders.reviewOrders;
export const selectReviewOrdersLoading = (state: RootState): boolean =>
    state.mcsReviewOrders.loading;
export const selectReviewOrdersError = (state: RootState): string | null =>
    state.mcsReviewOrders.error;

export default reviewOrdersSlice.reducer;
